import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useTheme } from '../contexts/ThemeContext';
import { usePractice } from '../contexts/PracticeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Clock, Activity, AlertCircle, Calendar, User, ChevronDown, ChevronLeft, ChevronRight, Users, Stethoscope, Info, HelpCircle } from 'lucide-react';
import { formatTime } from '../lib/utils';
import { Button } from '../components/ui/button';

const DENTAL_PULSE_LOGO = 'https://customer-assets.emergentagent.com/job_dentalpulse-4/artifacts/3espo6gq_DentalPulse_logoFinal%20%2B%20white.png';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DATE_RANGES = [
  { label: 'Today', value: 'day', days: 1 },
  { label: 'This Week', value: 'week', days: 7 },
  { label: 'This Month', value: 'month', days: 30 },
  { label: 'This Year', value: 'year', days: 365 },
  { label: 'All Time', value: 'all', days: 9999 },
  { label: 'Custom Range', value: 'custom', days: null },
];

const stateLabels = {
  patient_arrived: 'Patient Arrived',
  patient_in_room: 'In Room',
  seated: 'Seated',
  hygiene_check_needed: 'Hygiene Check',
  doctor_exam: 'Doctor Exam',
  assistant_help: 'Help Needed',
  dr_needed: 'Dr. Needed',
  patient_checkout: 'Checkout',
  recall_scheduling: 'Recall Scheduling'
};

function getFirstName(user) {
  if (!user) return '';
  if (user.first_name) return user.first_name;
  if (user.full_name) {
    const nameParts = user.full_name.split(' ');
    return nameParts[0] || '';
  }
  return '';
}

function getLastName(user) {
  if (!user) return '';
  if (user.last_name) return user.last_name;
  if (user.full_name) {
    const nameParts = user.full_name.split(' ');
    if (nameParts.length > 1) {
      return nameParts[nameParts.length - 1];
    }
  }
  return '';
}

function formatProviderDisplayName(provider) {
  if (!provider) return 'Unknown';
  
  const info = provider.provider_info || provider;
  const isDoctor = info.role === 'doctor' || info.base_role === 'doctor';
  
  if (isDoctor) {
    const preference = info.dr_display_preference || 'last';
    let displayName;
    if (preference === 'first') {
      displayName = getFirstName(info);
    } else {
      displayName = getLastName(info);
    }
    return 'Dr. ' + displayName;
  }
  
  if (provider.provider_name) return provider.provider_name;
  const firstName = getFirstName(info);
  if (firstName) return firstName;
  return info.full_name || 'Unknown';
}

// Tooltip component for info icons
function InfoTooltip({ text, darkMode }) {
  const [isVisible, setIsVisible] = useState(false);
  
  return (
    <div className="relative inline-block">
      <button
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className={`w-4 h-4 rounded-full flex items-center justify-center transition-colors ${
          darkMode ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'
        }`}
        data-testid="info-tooltip-trigger"
      >
        <Info className="w-3.5 h-3.5" />
      </button>
      {isVisible && (
        <div className={`absolute z-50 w-64 p-3 text-xs rounded-lg shadow-xl -top-2 right-6 ${
          darkMode ? 'bg-slate-700 text-slate-200 border border-slate-600' : 'bg-white text-slate-700 border border-slate-200'
        }`}>
          {text}
        </div>
      )}
    </div>
  );
}

// Merge/Split icon button
function MergeSplitButton({ isMerged, tooltipText, onClick, darkMode }) {
  const [isVisible, setIsVisible] = useState(false);
  
  return (
    <div className="relative inline-block">
      <button
        onClick={onClick}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className={`w-5 h-5 flex items-center justify-center transition-colors ${
          darkMode ? 'text-slate-500 hover:text-dp-400' : 'text-slate-400 hover:text-dp-600'
        }`}
        data-testid="merge-split-button"
      >
        {isMerged ? (
          // Split icon: <>
          <span className="font-mono text-sm font-bold">&lt;&gt;</span>
        ) : (
          // Merge icon: ><
          <span className="font-mono text-sm font-bold">&gt;&lt;</span>
        )}
      </button>
      {isVisible && (
        <div className={`absolute z-50 w-64 p-3 text-xs rounded-lg shadow-xl -top-2 right-6 ${
          darkMode ? 'bg-slate-700 text-slate-200 border border-slate-600' : 'bg-white text-slate-700 border border-slate-200'
        }`}>
          {tooltipText}
        </div>
      )}
    </div>
  );
}

// New metric card component with info tooltips
function MetricCard({ title, value, subtitle, infoText, darkMode, showMergeSplit, isMerged, mergeTooltip, onMergeSplitClick }) {
  return (
    <Card className={`border-0 relative ${darkMode ? 'bg-slate-800/50' : 'glass-panel'}`} data-testid={`metric-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-dp-600" />
            <span className={`text-base ${darkMode ? 'text-slate-200' : ''}`}>{title}</span>
          </div>
          <InfoTooltip text={infoText} darkMode={darkMode} />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className={`text-3xl font-mono font-bold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
          {value}
        </div>
        <div className="flex items-center justify-between mt-1">
          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>{subtitle}</p>
          {showMergeSplit && (
            <MergeSplitButton 
              isMerged={isMerged} 
              tooltipText={mergeTooltip} 
              onClick={onMergeSplitClick}
              darkMode={darkMode}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function DateRangeSelector({ selected, onSelect, customStart, customEnd, onCustomChange, darkMode }) {
  const [isOpen, setIsOpen] = useState(false);
  const closeTimeoutRef = React.useRef(null);
  
  let selectedRange = DATE_RANGES[1]; // Default to 'This Week'
  for (let i = 0; i < DATE_RANGES.length; i++) {
    if (DATE_RANGES[i].value === selected) {
      selectedRange = DATE_RANGES[i];
      break;
    }
  }
  
  const handleSelect = (rangeValue) => {
    onSelect(rangeValue);
    if (rangeValue !== 'custom') {
      setIsOpen(false);
    }
  };

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };
  
  return (
    <div 
      className="relative z-50"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Button
        onClick={() => setIsOpen(!isOpen)}
        variant="outline"
        className="flex items-center gap-2 min-w-[160px] justify-between"
        data-testid="date-range-selector"
      >
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          <span>{selectedRange.label}</span>
        </div>
        <ChevronDown className={'w-4 h-4 transition-transform ' + (isOpen ? 'rotate-180' : '')} />
      </Button>
      
      {isOpen && (
        <div className={`absolute top-full left-0 mt-2 w-72 rounded-lg shadow-xl py-2 z-[9999] ${
          darkMode ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-slate-200'
        }`}>
          {DATE_RANGES.map((range) => (
            <button
              key={range.value}
              onClick={() => handleSelect(range.value)}
              className={'w-full px-4 py-2 text-left text-sm ' + 
                (selected === range.value 
                  ? (darkMode ? 'bg-dp-900/50 text-dp-400 font-medium' : 'bg-dp-100 text-dp-700 font-medium')
                  : (darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-dp-50')
                )}
              data-testid={'date-range-' + range.value}
            >
              {range.label}
            </button>
          ))}
          
          {selected === 'custom' && (
            <div className={`px-4 py-3 space-y-3 ${darkMode ? 'border-t border-slate-700' : 'border-t border-slate-100'}`}>
              <div>
                <label className={`text-xs block mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Start Date</label>
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => onCustomChange('start', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md text-sm ${
                    darkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : 'bg-white border-slate-200 text-slate-800'
                  }`}
                  data-testid="custom-start-date"
                />
              </div>
              <div>
                <label className={`text-xs block mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>End Date</label>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => onCustomChange('end', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md text-sm ${
                    darkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : 'bg-white border-slate-200 text-slate-800'
                  }`}
                  data-testid="custom-end-date"
                />
              </div>
              <Button onClick={() => setIsOpen(false)} size="sm" className="w-full">
                Apply
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProviderSelector({ providers, selected, onSelect, darkMode }) {
  const [isOpen, setIsOpen] = useState(false);
  const closeTimeoutRef = React.useRef(null);
  
  let selectedProvider = null;
  if (selected) {
    for (let i = 0; i < providers.length; i++) {
      if (providers[i].id === selected) {
        selectedProvider = providers[i];
        break;
      }
    }
  }
  
  const formatName = (provider) => {
    if (!provider) return 'All Providers';
    const isDoctor = provider.role === 'doctor' || provider.base_role === 'doctor';
    if (isDoctor) {
      const preference = provider.dr_display_preference || 'last';
      let displayName;
      if (preference === 'first') {
        displayName = getFirstName(provider);
      } else {
        displayName = getLastName(provider);
      }
      return 'Dr. ' + displayName;
    }
    const firstName = getFirstName(provider);
    if (firstName) return firstName;
    return provider.full_name || 'Unknown';
  };
  
  const handleSelectProvider = (providerId) => {
    onSelect(providerId);
    setIsOpen(false);
  };

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };
  
  return (
    <div 
      className="relative z-50"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Button
        onClick={() => setIsOpen(!isOpen)}
        variant="outline"
        className="flex items-center gap-2 min-w-[180px] justify-between"
        data-testid="provider-selector"
      >
        <div className="flex items-center gap-2">
          <User className="w-4 h-4" />
          <span>{formatName(selectedProvider)}</span>
        </div>
        <ChevronDown className={'w-4 h-4 transition-transform ' + (isOpen ? 'rotate-180' : '')} />
      </Button>
      
      {isOpen && (
        <div className={`absolute top-full left-0 mt-2 w-56 rounded-lg shadow-xl py-2 z-[9999] max-h-64 overflow-y-auto ${
          darkMode ? 'bg-slate-800 border border-slate-700 dark-scrollbar' : 'bg-white border border-slate-200 light-scrollbar'
        }`}>
          <button
            onClick={() => handleSelectProvider(null)}
            className={'w-full px-4 py-2 text-left text-sm ' + 
              (!selected 
                ? (darkMode ? 'bg-dp-900/50 text-dp-400 font-medium' : 'bg-dp-100 text-dp-700 font-medium')
                : (darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-dp-50')
              )}
            data-testid="provider-all"
          >
            All Providers
          </button>
          {providers.map((provider) => (
            <button
              key={provider.id}
              onClick={() => handleSelectProvider(provider.id)}
              className={'w-full px-4 py-2 text-left text-sm ' + 
                (selected === provider.id 
                  ? (darkMode ? 'bg-dp-900/50 text-dp-400 font-medium' : 'bg-dp-100 text-dp-700 font-medium')
                  : (darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-dp-50')
                )}
              data-testid={'provider-' + provider.id}
            >
              {formatName(provider)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

 

function ProviderReportCard({ provider, darkMode }) {
  const [expandedSection, setExpandedSection] = useState(null);
  const isDoctor = provider.role === 'doctor';
  const metrics = provider.metrics || {};
  
  // Metric definitions based on role
  const doctorMetrics = [
    { key: 'patient_arrived', label: 'Patient Arrived', icon: Clock },
    { key: 'patient_in_room', label: 'In Room', icon: Clock },
    { key: 'hygiene_check', label: 'Hygiene Check', icon: Clock },
    { key: 'dr_exam_time', label: 'Dr. Exam Time', icon: Stethoscope, expandable: true, breakdownKey: 'hygienist_breakdown' },
    { key: 'new_patient_exam_time', label: 'New Patient Exam', icon: Stethoscope, expandable: true, breakdownKey: 'hygienist_breakdown' },
    { key: 'help_needed', label: 'Help Needed', icon: HelpCircle }
  ];
  
  const hygienistMetrics = [
    { key: 'patient_arrived', label: 'Patient Arrived', icon: Clock },
    { key: 'patient_in_room', label: 'In Room', icon: Clock },
    { key: 'dr_exam_time', label: 'Dr. Exam Time', icon: Stethoscope, expandable: true, breakdownKey: 'doctor_breakdown' },
    { key: 'new_patient_exam_time', label: 'New Patient Exam', icon: Stethoscope, expandable: true, breakdownKey: 'doctor_breakdown' },
    { key: 'help_needed', label: 'Help Needed', icon: HelpCircle }
  ];
  
  const metricsToShow = isDoctor ? doctorMetrics : hygienistMetrics;
  const breakdown = isDoctor ? provider.hygienist_breakdown : provider.doctor_breakdown;
  
  const toggleExpand = (key) => {
    setExpandedSection(expandedSection === key ? null : key);
  };
  
  return (
    <Card className={`glass-panel border-0 ${darkMode ? 'bg-slate-800/50' : ''}`} data-testid={'provider-card-' + provider.provider_id}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-3">
          {isDoctor ? (
            <Stethoscope className="w-5 h-5 text-indigo-500" />
          ) : (
            <User className="w-5 h-5 text-emerald-500" />
          )}
          <span className={`text-lg ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
            {provider.provider_name}
          </span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            isDoctor 
              ? (darkMode ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-700')
              : (darkMode ? 'bg-emerald-900/50 text-emerald-300' : 'bg-emerald-100 text-emerald-700')
          }`}>
            {isDoctor ? 'Doctor' : 'Hygienist'}
          </span>
        </CardTitle>
        <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
          {provider.total_exams} exams recorded
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {metricsToShow.map((metric) => {
            const value = metrics[metric.key] || 0;
            const Icon = metric.icon;
            const isExpanded = expandedSection === metric.key;
            
            return (
              <div key={metric.key}>
                <div 
                  className={`rounded-lg p-3 transition-all ${
                    darkMode ? 'bg-slate-700/50' : 'bg-slate-50'
                  } ${metric.expandable ? 'cursor-pointer hover:ring-2 ring-dp-400' : ''}`}
                  onClick={() => metric.expandable && toggleExpand(metric.key)}
                >
                  <div className="flex items-center justify-between">
                    <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'} truncate flex items-center gap-1`}>
                      <Icon className="w-3 h-3" />
                      {metric.label}
                    </p>
                    {metric.expandable && (
                      <ChevronDown className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''} ${darkMode ? 'text-slate-400' : 'text-slate-500'}`} />
                    )}
                  </div>
                  <p className={`text-xl font-mono font-bold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                    {formatTime(Math.round(value))}
                  </p>
                </div>
                
                {/* Expandable breakdown section */}
                {metric.expandable && isExpanded && breakdown && breakdown.length > 0 && (
                  <div className={`mt-2 rounded-lg p-3 ${darkMode ? 'bg-slate-600/50' : 'bg-slate-100'}`}>
                    <p className={`text-xs font-semibold mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      {isDoctor ? 'By Hygienist' : 'By Doctor'}
                    </p>
                    <div className="space-y-2">
                      {breakdown.map((item) => {
                        const itemKey = isDoctor ? item.hygienist_id : item.doctor_id;
                        const itemName = isDoctor ? item.hygienist_name : item.doctor_name;
                        const avgTime = metric.key === 'new_patient_exam_time' 
                          ? item.avg_new_patient_time 
                          : item.avg_exam_time;
                        const count = metric.key === 'new_patient_exam_time'
                          ? item.new_patient_count
                          : item.exam_count;
                        
                        if (count === 0) return null;
                        
                        return (
                          <div key={itemKey} className={`flex items-center justify-between text-xs ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            <span className="truncate">{itemName}</span>
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold">{formatTime(Math.round(avgTime))}</span>
                              <span className={`${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>({count})</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default function ReportsPage() {
  const { darkMode } = useTheme();
  const { practice } = usePractice();
  const [practiceReports, setPracticeReports] = useState(null);
  const [providerReports, setProviderReports] = useState(null);
  const [detailedProviderReports, setDetailedProviderReports] = useState(null);
  const [ranLongMetrics, setRanLongMetrics] = useState(null);
  const [doctorExamMetrics, setDoctorExamMetrics] = useState(null);
  const [allProviders, setAllProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('week');
  const [periodOffset, setPeriodOffset] = useState(0); // 0 = current period, -1 = previous, etc.
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [activeTab, setActiveTab] = useState('practice');
  const [isMergedView, setIsMergedView] = useState(false);
  
  // Check if Dr. In Room tracking is enabled
  const drInRoomEnabled = practice?.dr_in_room_enabled || false;

  // Calculate the date range based on selection and offset
  const getDateRangeForPeriod = () => {
    const now = new Date();
    let startDate, endDate;
    
    switch (dateRange) {
      case 'day':
        // Single day
        const dayDate = new Date(now);
        dayDate.setDate(dayDate.getDate() + periodOffset);
        startDate = new Date(dayDate);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(dayDate);
        endDate.setHours(23, 59, 59, 999);
        break;
        
      case 'week':
        // Week (Sunday to Saturday)
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + (periodOffset * 7));
        weekStart.setHours(0, 0, 0, 0);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);
        startDate = weekStart;
        endDate = weekEnd;
        break;
        
      case 'month':
        // Month
        const monthDate = new Date(now.getFullYear(), now.getMonth() + periodOffset, 1);
        startDate = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
        endDate = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0, 23, 59, 59, 999);
        break;
        
      case 'year':
        // Year
        const yearDate = new Date(now.getFullYear() + periodOffset, 0, 1);
        startDate = new Date(yearDate.getFullYear(), 0, 1);
        endDate = new Date(yearDate.getFullYear(), 11, 31, 23, 59, 59, 999);
        break;
        
      default:
        // All time or custom - no navigation
        return null;
    }
    
    return { startDate, endDate };
  };

  // Format the date display based on range type
  const getDateDisplayText = () => {
    const range = getDateRangeForPeriod();
    if (!range) return null;
    
    const { startDate, endDate } = range;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    switch (dateRange) {
      case 'day':
        // Format: "Mon, Jan 15"
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        return `${dayNames[startDate.getDay()]}, ${months[startDate.getMonth()]} ${startDate.getDate()}`;
        
      case 'week':
        // Format: "Jan 12 - Jan 18"
        if (startDate.getMonth() === endDate.getMonth()) {
          return `${months[startDate.getMonth()]} ${startDate.getDate()} - ${endDate.getDate()}`;
        }
        return `${months[startDate.getMonth()]} ${startDate.getDate()} - ${months[endDate.getMonth()]} ${endDate.getDate()}`;
        
      case 'month':
        // Format: "Jan '25"
        return `${months[startDate.getMonth()]} '${String(startDate.getFullYear()).slice(-2)}`;
        
      case 'year':
        // Format: "2025"
        return String(startDate.getFullYear());
        
      default:
        return null;
    }
  };

  // Navigate to previous/next period
  const handlePreviousPeriod = () => {
    setPeriodOffset(prev => prev - 1);
  };

  const handleNextPeriod = () => {
    // Don't allow going into the future
    if (periodOffset < 0) {
      setPeriodOffset(prev => prev + 1);
    }
  };

  // Reset offset when date range type changes
  useEffect(() => {
    setPeriodOffset(0);
  }, [dateRange]);

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = {};
      
      // Use calculated date range if navigating, otherwise use days
      const calculatedRange = getDateRangeForPeriod();
      
      if (dateRange === 'custom' && customStart && customEnd) {
        params.start_date = new Date(customStart).toISOString();
        const endDateStr = customEnd + 'T23:59:59';
        params.end_date = new Date(endDateStr).toISOString();
      } else if (calculatedRange && (dateRange === 'day' || dateRange === 'week' || dateRange === 'month' || dateRange === 'year')) {
        // Use exact date range for navigable periods
        params.start_date = calculatedRange.startDate.toISOString();
        params.end_date = calculatedRange.endDate.toISOString();
      } else {
        // Fallback to days-based calculation for "all time"
        let days = 9999;
        for (let i = 0; i < DATE_RANGES.length; i++) {
          if (DATE_RANGES[i].value === dateRange && DATE_RANGES[i].days) {
            days = DATE_RANGES[i].days;
            break;
          }
        }
        params.days = days;
      }
      
      if (selectedProvider) {
        params.provider_id = selectedProvider;
      }
      
      const providerParams = {};
      if (params.start_date) {
        providerParams.start_date = params.start_date;
        providerParams.end_date = params.end_date;
      } else {
        providerParams.days = params.days;
      }
      
      const [practiceRes, providerRes, ranLongRes, examTimesRes, detailedProviderRes] = await Promise.all([
        axios.get(API + '/reports/wait-times', { params }),
        axios.get(API + '/reports/providers', { params: providerParams }),
        axios.get(API + '/reports/ran-long-metrics', { params }),
        axios.get(API + '/reports/doctor-exam-times', { params }),
        axios.get(API + '/reports/providers-detailed', { params: providerParams })
      ]);
      
      setPracticeReports(practiceRes.data);
      setProviderReports(providerRes.data);
      setDetailedProviderReports(detailedProviderRes.data);
      setRanLongMetrics(ranLongRes.data);
      setDoctorExamMetrics(examTimesRes.data);
      setAllProviders(providerRes.data.all_providers || []);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, customStart, customEnd, selectedProvider, periodOffset]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleCustomChange = (field, value) => {
    if (field === 'start') {
      setCustomStart(value);
    } else if (field === 'end') {
      setCustomEnd(value);
    }
  };

  if (loading && !practiceReports) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Activity className="w-12 h-12 animate-spin text-dp-600" />
      </div>
    );
  }

  let selectedProviderInfo = null;
  if (selectedProvider) {
    for (let i = 0; i < allProviders.length; i++) {
      if (allProviders[i].id === selectedProvider) {
        selectedProviderInfo = allProviders[i];
        break;
      }
    }
  }

  // Get doctors and hygienists from detailed reports
  const doctorsList = detailedProviderReports?.doctors || [];
  const hygienistsList = detailedProviderReports?.hygienists || [];

  // Apply custom background from practice settings
  const bgStyle = darkMode ? {
    backgroundColor: '#0f172a',
    backgroundImage: practice?.background_image_dark ? `url(${practice.background_image_dark})` : 'none',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed'
  } : {
    backgroundColor: practice?.background_color || '#f8fafc',
    backgroundImage: practice?.background_image ? `url(${practice.background_image})` : 'none',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed'
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''}`} style={bgStyle}>
      <Sidebar />
      
      <div className="pl-28 pr-8 pt-6 pb-8">
        <div className={`glass-panel rounded-2xl p-6 mb-6 flex items-center justify-between ${darkMode ? 'bg-slate-800/50' : ''}`}>
          <div>
            <h1 className={`text-3xl font-heading font-bold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>Reports & Analytics</h1>
            <p className={`mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Track performance metrics and wait times</p>
          </div>
          <div className="flex-shrink-0 ml-4">
            <img 
              src={DENTAL_PULSE_LOGO} 
              alt="DentalPulse"
              className="h-12 w-auto object-contain"
              data-testid="dentalpulse-logo"
            />
          </div>
        </div>

        <div className={`glass-panel rounded-2xl p-4 mb-6 relative z-[100] ${darkMode ? 'bg-slate-800/50' : ''}`}>
          <div className="flex flex-wrap items-center gap-4">
            <DateRangeSelector
              selected={dateRange}
              onSelect={setDateRange}
              customStart={customStart}
              customEnd={customEnd}
              onCustomChange={handleCustomChange}
              darkMode={darkMode}
            />
            
            {/* Date Navigator - only show for day/week/month/year */}
            {(dateRange === 'day' || dateRange === 'week' || dateRange === 'month' || dateRange === 'year') && (
              <div className={`flex items-center gap-1 rounded-lg px-2 py-1 ${darkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>
                <button
                  onClick={handlePreviousPeriod}
                  className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${
                    darkMode 
                      ? 'hover:bg-slate-600 text-slate-300 hover:text-white' 
                      : 'hover:bg-white text-slate-600 hover:text-slate-800'
                  }`}
                  data-testid="nav-previous"
                  title="Previous period"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className={`min-w-[120px] text-center text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {getDateDisplayText()}
                </span>
                <button
                  onClick={handleNextPeriod}
                  disabled={periodOffset >= 0}
                  className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${
                    periodOffset >= 0
                      ? (darkMode ? 'text-slate-600 cursor-not-allowed' : 'text-slate-300 cursor-not-allowed')
                      : (darkMode 
                          ? 'hover:bg-slate-600 text-slate-300 hover:text-white' 
                          : 'hover:bg-white text-slate-600 hover:text-slate-800')
                  }`}
                  data-testid="nav-next"
                  title="Next period"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
            
            <ProviderSelector
              providers={allProviders}
              selected={selectedProvider}
              onSelect={setSelectedProvider}
              darkMode={darkMode}
            />

            <div className={`ml-auto flex rounded-lg p-1 ${darkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>
              <button
                onClick={() => setActiveTab('practice')}
                className={'px-4 py-2 rounded-md text-sm font-medium transition-colors ' +
                  (activeTab === 'practice' 
                    ? (darkMode ? 'bg-slate-600 text-slate-100 shadow-sm' : 'bg-white text-slate-800 shadow-sm')
                    : (darkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-800'))}
                data-testid="tab-practice"
              >
                Practice Overview
              </button>
              <button
                onClick={() => setActiveTab('providers')}
                className={'px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ' +
                  (activeTab === 'providers' 
                    ? (darkMode ? 'bg-slate-600 text-slate-100 shadow-sm' : 'bg-white text-slate-800 shadow-sm')
                    : (darkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-800'))}
                data-testid="tab-providers"
              >
                <Users className="w-4 h-4" />
                By Provider
              </button>
            </div>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-8">
            <Activity className="w-8 h-8 animate-spin text-dp-600" />
          </div>
        )}

        {activeTab === 'practice' && !loading && (
          <>
            {/* Top 6 Metric Boxes (2 rows x 3 columns) */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {/* Row 1 */}
              {isMergedView ? (
                // Merged: Total Patient Waiting Time (replaces Patient Arrived + In Room)
                <MetricCard
                  title="Total Patient Waiting Time"
                  value={formatTime(Math.round(
                    (practiceReports?.average_wait_times?.patient_arrived || 0) + 
                    (practiceReports?.average_wait_times?.patient_in_room || 0)
                  ))}
                  subtitle="Average Wait Time"
                  infoText="Total Patient Waiting Time calculates the time from when a patient arrived at the office to when they were seen by the doctor. To split this into time waited in the reception area and time waited in the operatory, click the divide icon in the bottom right of this box."
                  darkMode={darkMode}
                  showMergeSplit={true}
                  isMerged={true}
                  mergeTooltip="Clicking this will separate Total Patient Waiting Time into time waited in the reception area and time waited in the operatory."
                  onMergeSplitClick={() => setIsMergedView(false)}
                />
              ) : (
                // Separate: Patient Arrived
                <MetricCard
                  title="Patient Arrived"
                  value={formatTime(Math.round(practiceReports?.average_wait_times?.patient_arrived || 0))}
                  subtitle="Average Wait Time"
                  infoText="Patient Arrived is measured as the average wait time a patient has spent in the office prior to being seated."
                  darkMode={darkMode}
                  showMergeSplit={true}
                  isMerged={false}
                  mergeTooltip="Clicking this will merge Patient Arrived and In Room to create Total Patient Waiting Time."
                  onMergeSplitClick={() => setIsMergedView(true)}
                />
              )}
              
              {!isMergedView && (
                // In Room (only shown when not merged)
                <MetricCard
                  title="In Room"
                  value={drInRoomEnabled 
                    ? formatTime(Math.round(practiceReports?.average_wait_times?.patient_in_room || 0))
                    : '__:__'}
                  subtitle="Average Wait Time"
                  infoText="In Room is measured as the average wait time a patient has spent in the operatory prior to being seen by the doctor. If 'Dr. In Room' status is NOT enabled this will display __:__. Go to settings to turn on 'Dr. In Room' to begin collecting data."
                  darkMode={darkMode}
                />
              )}
              
              {/* Hygiene Check */}
              <MetricCard
                title="Hygiene Check"
                value={drInRoomEnabled 
                  ? formatTime(Math.round(practiceReports?.average_wait_times?.hygiene_check_needed || practiceReports?.average_wait_times?.dr_needed || 0))
                  : '__:__'}
                subtitle="Average Wait Time"
                infoText="Hygiene Check is measured as the average wait time a hygienist and patient have waited for the doctor to arrive for an exam. If 'Dr. In Room' status is NOT enabled this will display __:__. Go to settings to turn on 'Dr. In Room' to begin collecting data."
                darkMode={darkMode}
              />
              
              {/* Row 2 */}
              {/* Dr. Exam Time (non-NP exams) */}
              <MetricCard
                title="Dr. Exam Time"
                value={drInRoomEnabled 
                  ? formatTime(Math.round(
                      // Calculate non-NP exam average: total - NP portion
                      doctorExamMetrics?.total_exams > 0 && doctorExamMetrics?.new_patient_total_exams < doctorExamMetrics?.total_exams
                        ? ((doctorExamMetrics?.average_exam_seconds || 0) * doctorExamMetrics?.total_exams - 
                           (doctorExamMetrics?.new_patient_average_seconds || 0) * (doctorExamMetrics?.new_patient_total_exams || 0)) /
                          (doctorExamMetrics?.total_exams - (doctorExamMetrics?.new_patient_total_exams || 0))
                        : (doctorExamMetrics?.average_exam_seconds || 0)
                    ))
                  : '__:__'}
                subtitle="Average Exam Time"
                infoText="Dr. Exam Time is measured as the average length of all non-new patient exams. If 'Dr. In Room' status is NOT enabled this will display __:__. Go to settings to turn on 'Dr. In Room' to begin collecting data."
                darkMode={darkMode}
              />
              
              {/* New Patient Exam Time */}
              <MetricCard
                title="New Patient Exam Time"
                value={drInRoomEnabled 
                  ? formatTime(Math.round(doctorExamMetrics?.new_patient_average_seconds || 0))
                  : '__:__'}
                subtitle="Average Exam Time"
                infoText="New Patient Exam Time is measured as the average length of all new patient exams completed in hygiene operatories. If 'Dr. In Room' status is NOT enabled this will display __:__. Go to settings to turn on 'Dr. In Room' to begin collecting data."
                darkMode={darkMode}
              />
              
              {/* Help Needed - always shown as 6th box */}
              <MetricCard
                title="Help Needed"
                value={formatTime(Math.round(practiceReports?.average_wait_times?.assistant_help || 0))}
                subtitle="Average Wait Time"
                infoText="Help Needed is measured as the average time from when the help button was pressed to when the request was cleared."
                darkMode={darkMode}
              />
            </div>

            {/* Appointments Ran Long Metrics */}
            {ranLongMetrics && ranLongMetrics.total_appointments > 0 && (
              <div className={`glass-panel rounded-2xl p-6 mt-6 ${darkMode ? 'bg-slate-800/50' : ''}`}>
                <h2 className={`text-xl font-heading font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                  Appointments Running Long
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className={`rounded-lg p-4 ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                    <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Total Appointments</p>
                    <p className={`text-2xl font-bold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>{ranLongMetrics.total_appointments}</p>
                  </div>
                  <div className={`rounded-lg p-4 ${darkMode ? 'bg-amber-900/30' : 'bg-amber-50'}`}>
                    <p className={`text-sm ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>Ran Long</p>
                    <p className={`text-2xl font-bold ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>{ranLongMetrics.ran_long_count}</p>
                  </div>
                  <div className={`rounded-lg p-4 ${darkMode ? 'bg-amber-900/30' : 'bg-amber-50'}`}>
                    <p className={`text-sm ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>% Running Long</p>
                    <p className={`text-2xl font-bold ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>{ranLongMetrics.ran_long_percentage}%</p>
                  </div>
                  <div className={`rounded-lg p-4 ${darkMode ? 'bg-red-900/30' : 'bg-red-50'}`}>
                    <p className={`text-sm ${darkMode ? 'text-red-400' : 'text-red-600'}`}>Avg. Minutes Over</p>
                    <p className={`text-2xl font-bold ${darkMode ? 'text-red-300' : 'text-red-700'}`}>{ranLongMetrics.average_minutes_over} min</p>
                  </div>
                </div>

                {ranLongMetrics.by_provider && ranLongMetrics.by_provider.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">By Provider</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-2 px-3 text-slate-600 font-medium">Provider</th>
                            <th className="text-right py-2 px-3 text-slate-600 font-medium">Total</th>
                            <th className="text-right py-2 px-3 text-slate-600 font-medium">Ran Long</th>
                            <th className="text-right py-2 px-3 text-slate-600 font-medium">% Long</th>
                            <th className="text-right py-2 px-3 text-slate-600 font-medium">Avg. Over</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ranLongMetrics.by_provider.map((provider) => (
                            <tr key={provider.provider_id} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-2 px-3 font-medium text-slate-800">{provider.provider_name}</td>
                              <td className="py-2 px-3 text-right text-slate-600">{provider.total_appointments}</td>
                              <td className="py-2 px-3 text-right text-amber-600">{provider.ran_long_count}</td>
                              <td className="py-2 px-3 text-right">
                                <span className={`font-medium ${provider.ran_long_percentage > 20 ? 'text-red-600' : provider.ran_long_percentage > 10 ? 'text-amber-600' : 'text-emerald-600'}`}>
                                  {provider.ran_long_percentage}%
                                </span>
                              </td>
                              <td className="py-2 px-3 text-right text-slate-600">
                                {provider.ran_long_count > 0 ? provider.average_minutes_over + ' min' : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Doctor Exam Times Metrics */}
            {doctorExamMetrics && doctorExamMetrics.total_exams > 0 && (
              <div className={`glass-panel rounded-2xl p-6 mt-6 ${darkMode ? 'bg-slate-800/50' : ''}`} data-testid="doctor-exam-metrics">
                <h2 className={`text-xl font-heading font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                  <Stethoscope className="w-5 h-5 text-indigo-500" />
                  Doctor Exam Times
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className={`rounded-lg p-4 ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                    <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Total Exams</p>
                    <p className={`text-2xl font-bold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>{doctorExamMetrics.total_exams}</p>
                  </div>
                  <div className={`rounded-lg p-4 ${darkMode ? 'bg-indigo-900/30' : 'bg-indigo-50'}`}>
                    <p className={`text-sm ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>Average Exam Time</p>
                    <p className={`text-2xl font-bold font-mono ${darkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>{formatTime(doctorExamMetrics.average_exam_seconds || 0)}</p>
                  </div>
                  <div className={`rounded-lg p-4 ${darkMode ? 'bg-indigo-900/30' : 'bg-indigo-50'}`}>
                    <p className={`text-sm ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>Longest Exam</p>
                    <p className={`text-2xl font-bold font-mono ${darkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>{formatTime(doctorExamMetrics.longest_exam_seconds || 0)}</p>
                  </div>
                </div>

                {doctorExamMetrics.by_doctor && doctorExamMetrics.by_doctor.length > 0 && (
                  <div>
                    <h3 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>By Doctor</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className={`border-b ${darkMode ? 'border-slate-600' : 'border-slate-200'}`}>
                            <th className={`text-left py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Doctor</th>
                            <th className={`text-right py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Total Exams</th>
                            <th className={`text-right py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Avg. Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {doctorExamMetrics.by_doctor.map((doctor) => (
                            <tr key={doctor.doctor_id} className={`border-b ${darkMode ? 'border-slate-700 hover:bg-slate-700/50' : 'border-slate-100 hover:bg-slate-50'}`}>
                              <td className={`py-2 px-3 font-medium ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>{doctor.doctor_name}</td>
                              <td className={`py-2 px-3 text-right ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{doctor.total_exams}</td>
                              <td className={`py-2 px-3 text-right font-mono ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>{formatTime(doctor.average_exam_seconds || 0)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* New Patient Exam Times Metrics */}
            {doctorExamMetrics && doctorExamMetrics.new_patient_total_exams > 0 && (
              <div className={`glass-panel rounded-2xl p-6 mt-6 ${darkMode ? 'bg-slate-800/50' : ''}`} data-testid="new-patient-exam-metrics">
                <h2 className={`text-xl font-heading font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                  <span className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center text-white text-xs font-bold">NP</span>
                  New Patient Exam Times
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className={`rounded-lg p-4 ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                    <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Total NP Exams</p>
                    <p className={`text-2xl font-bold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>{doctorExamMetrics.new_patient_total_exams}</p>
                  </div>
                  <div className={`rounded-lg p-4 ${darkMode ? 'bg-amber-900/30' : 'bg-amber-50'}`}>
                    <p className={`text-sm ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>Average NP Exam Time</p>
                    <p className={`text-2xl font-bold font-mono ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>{formatTime(doctorExamMetrics.new_patient_average_seconds || 0)}</p>
                  </div>
                  <div className={`rounded-lg p-4 ${darkMode ? 'bg-amber-900/30' : 'bg-amber-50'}`}>
                    <p className={`text-sm ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>Longest NP Exam</p>
                    <p className={`text-2xl font-bold font-mono ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>{formatTime(doctorExamMetrics.new_patient_longest_seconds || 0)}</p>
                  </div>
                </div>

                {doctorExamMetrics.new_patient_by_doctor && doctorExamMetrics.new_patient_by_doctor.length > 0 && (
                  <div>
                    <h3 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>By Doctor</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className={`border-b ${darkMode ? 'border-slate-600' : 'border-slate-200'}`}>
                            <th className={`text-left py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Doctor</th>
                            <th className={`text-right py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>NP Exams</th>
                            <th className={`text-right py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Avg. Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {doctorExamMetrics.new_patient_by_doctor.map((doctor) => (
                            <tr key={doctor.doctor_id} className={`border-b ${darkMode ? 'border-slate-700 hover:bg-slate-700/50' : 'border-slate-100 hover:bg-slate-50'}`}>
                              <td className={`py-2 px-3 font-medium ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>{doctor.doctor_name}</td>
                              <td className={`py-2 px-3 text-right ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{doctor.total_exams}</td>
                              <td className={`py-2 px-3 text-right font-mono ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>{formatTime(doctor.average_exam_seconds || 0)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <p className={`text-xs mt-3 ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                  New patient exams are tracked separately but count towards overall exam metrics and wait times.
                </p>
              </div>
            )}
          </>
        )}

        {activeTab === 'providers' && !loading && (
          <>
            {(doctorsList.length > 0 || hygienistsList.length > 0) ? (
              <>
                {/* Doctors Section */}
                {doctorsList.length > 0 && (
                  <div className="mb-8">
                    <h2 className={`text-xl font-heading font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                      <Stethoscope className="w-5 h-5 text-indigo-500" />
                      Doctors ({doctorsList.length})
                    </h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {doctorsList.map((doctor) => (
                        <ProviderReportCard key={doctor.provider_id} provider={doctor} darkMode={darkMode} />
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Hygienists Section */}
                {hygienistsList.length > 0 && (
                  <div>
                    <h2 className={`text-xl font-heading font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                      <User className="w-5 h-5 text-emerald-500" />
                      Hygienists ({hygienistsList.length})
                    </h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {hygienistsList.map((hygienist) => (
                        <ProviderReportCard key={hygienist.provider_id} provider={hygienist} darkMode={darkMode} />
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className={`text-center py-12 glass-panel rounded-2xl ${darkMode ? 'bg-slate-800/50' : ''}`}>
                <Users className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-slate-600' : 'text-slate-400'}`} />
                <p className={darkMode ? 'text-slate-300' : 'text-slate-600'}>No provider data available for this period.</p>
                <p className={`text-sm mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                  Provider stats are recorded when appointments are completed with a provider assigned.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
